<template>
  <b-card>
    <h3>{{ $t("services.addHeaderTitle") }}</h3>
    <layout-loading v-if="itemLoading" />
    <!-- form -->
    <b-form>
      <b-row>
        <b-col md="4">
          <b-form-group
            :label="$t('general.title')"
            label-for="service-title"
            class="mb-2"
          >
            <b-form-input
              id="service-title"
              v-model="form.title"
              :state="validateState('title')"
              @change="updateAltImage()"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            :label="$t('general.slug')"
            label-for="service-slug"
            class="mb-2"
          >
            <b-form-input
              id="service-slug"
              v-model="form.slug"
              :state="validateState('slug')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.showOnHome')"
            label-for="service-showOnHome"
            class="mb-2"
          >
            <b-form-checkbox
              id="video-momMemories"
              class="mt-1"
              :checked="form.showOnHome"
              name="video-showOnHome"
              switch
              inline
              @input="handleShowOnHomeChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.waterMark')"
            label-for="article-watermark"
          >
            <b-form-checkbox
              id="article-watermark"
              v-model="form.watermark"
              class="mt-1"
              name="article-waterMark"
              switch
              inline
            />
            <b-form-invalid
              id="input-watermark-feedback"
              :label="$t('general.waterMark')"
              :validator="$v.form.watermark"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <service-priority-field v-model="form.sort" />
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="service-lang"
            class="mb-2"
          >
            <languageSelector
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.writer')"
            label-for="service-writer"
            :state="validateState('writer')"
          >
            <v-select-custom
              id="service-writer"
              v-model="form.writer"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'writer' }, form.lang)"
              @search="fetchUsersByRole({role: 'writer', 'search' : $event}, form.lang)"
            />
            <b-form-invalid
              id="input-writerId-feedback"
              :label="$t('general.writer')"
              :validator="$v.form.writer"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.category')"
            label-for="service-category"
            class="mb-2"
            :state="validateState('category')"
          >
            <v-select-custom
              id="service-category"
              v-model="form.category"
              :options="serviceCategories"
              @search="fetchServiceCategories(form.lang, $event)"
            />
            <b-form-invalid
              id="input-category-feedback"
              :label="$t('general.category')"
              :validator="$v.form.category"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.editor')"
            label-for="page-editor"
          >
            <v-select-custom
              id="page-editor"
              v-model="form.editor"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'editor' }, form.lang)"
              @search="fetchUsersByRole({role: 'editor', 'search' : $event}, form.lang)"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.tags')"
            label-for="service-tags"
            :description="optionalTagsHint()"
            class="mb-2"
            :state="validateState('tags')"
          >
            <v-select-custom
              id="service-tags"
              v-model="form.tags"
              :custom-props="{ multiple: true, taggable: true }"
              :options="tags"
              @search="fetchTags(form.lang, $event)"
              @option:created="addNewTag(form.lang, $event)"
            />
            <b-form-invalid
              id="input-tags-feedback"
              :label="$t('general.tags')"
              :validator="$v.form.tags"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="service-keywords"
            class="mb-2"
          >
            <b-form-input
              id="service-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          class="collapse-border"
        >
          <app-collapse>
            <app-collapse-item
              :is-visible="true"
              :title="$t('general.description')"
            >
              <b-form-group
                class="mb-2"
                :state="validateState('description')"
              >
                <BFormTextarea
                  id="service-description"
                  v-model="form.description"
                  rows="10"
                  :state="validateState('description')"
                />
                <b-form-invalid
                  id="input-description-feedback"
                  :label="$t('general.description')"
                  :validator="$v.form.description"
                />
              </b-form-group>
            </app-collapse-item>
          </app-collapse>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="service-content"
            class="mb-2"
          >
            <SunEditorBasic
              id="service-content"
              ref="sunEditor"
              v-model="form.content"
              :label="$t('general.content')"
              :state="validateState('content')"
            />
            <b-form-invalid
              id="input-content-feedback"
              :label="$t('general.content')"
              :validator="$v.form.content"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.thumbnail") }} - {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="serviceThumbnail"
              v-model="form.serviceThumbnail"
              :placeholder="$t('general.select')"
              :state="validateState('serviceThumbnail')"
              :state-alt="validateState('serviceThumbnailAlt')"
              :alt.sync="form.serviceThumbnailAlt"
              :alt-validator="$v.form.serviceThumbnailAlt"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-serviceThumbnail-feedback"
                  :label="$t('general.thumbnail')"
                  :validator="$v.form.serviceThumbnail"
                  :is-bootstrap="false"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.cover") }} - {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="service-cover-image"
              v-model="form.serviceCoverImage"
              :placeholder="$t('general.select')"
              :state="validateState('serviceCoverImage')"
              :state-alt="validateState('serviceCoverImageAlt')"
              :alt.sync="form.serviceCoverImageAlt"
              :alt-validator="$v.form.serviceCoverImageAlt"
              name="serviceCoverImage"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-service-cover-image-feedback"
                  :label="$t('general.cover')"
                  :validator="$v.form.serviceCoverImage"
                  :is-bootstrap="false"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BFormCheckbox, BFormTextarea, BRow,
} from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import SunEditorBasic from '@/components/SunEditorBasic/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import languageSelector from '@/components/language-selector.vue'
import { isValidImageSize } from '@core/utils/rules'
import { required, minLength, requiredIf } from 'vuelidate/lib/validators'
import ServicePriorityField from './ServicePriorityField.vue'
import serviceOptionalTagsHint from './serviceOptionalTagsHint'
import useServicesList from './useServicesList'

export default {
  components: {
    ServicePriorityField,
    AppCollapse,
    AppCollapseItem,
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormCheckbox,
    BFormTextarea,
    BRow,
    SunEditorBasic,
    LayoutLoading,
    VSelectCustom,
    languageSelector,
  },
  directives: {
    Ripple,
  },
  setup() {
    const {
      addItem,
      addNewTag,
      addTag,
      checkSlugIsUnique,
      createTagIdArray,
      errorManager,
      fetchServiceCategories,
      fetchTags,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      itemLoading,
      newTagIds,
      serviceCategories,
      tags,
      userOptions,
    } = useServicesList()
    return {
      addItem,
      addNewTag,
      addTag,
      checkSlugIsUnique,
      createTagIdArray,
      errorManager,
      fetchServiceCategories,
      fetchTags,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      itemLoading,
      newTagIds,
      serviceCategories,
      tags,
      userOptions,
    }
  },
  data() {
    return {
      editorOptions: [],
      writerOptions: [],
    }
  },
  validations() {
    return {
      form: {
        title: {
          required,
          minLength: minLength(3),
        },
        slug: {
          required,
          minLength: minLength(3),
          isUnique: () => this.isUniqueSlug,
        },
        tags: {},
        keywords: {
          required,
          minLength: minLength(3),
        },
        category: {
          required,
        },
        writer: {
          required,
        },
        description: {
          required,
          minLength: minLength(10),
        },
        content: {
          required,
          minLength: minLength(10),
        },
        serviceThumbnail: {
          required,
          isTrueImage: requiredIf(function () {
            return typeof this.form.serviceThumbnail === 'object' && !Object.prototype.hasOwnProperty.call(this.form.serviceThumbnail, '_id')
          }),
          isValidImageSize,
        },
        serviceThumbnailAlt: {
          required,
          minLength: minLength(3),
        },
        serviceCoverImage: {
          required,
          isTrueImage: requiredIf(function () {
            return typeof this.form.serviceCoverImage === 'object' && !Object.prototype.hasOwnProperty.call(this.form.serviceCoverImage, '_id')
          }),
          isValidImageSize,
        },
        serviceCoverImageAlt: {
          required,
          minLength: minLength(3),
        },
      },
    }
  },
  watch: {
    'form.title': function (newValue, oldValue) {
      this.$set(this.form, 'slug', this.createSlug(this.form.title))
      if (oldValue === this.form.serviceThumbnailAlt) {
        this.$set(this.form, 'serviceThumbnailAlt', newValue)
      }
      if (oldValue === this.form.serviceCoverImageAlt) {
        this.$set(this.form, 'serviceCoverImageAlt', newValue)
      }
    },
    'form.slug': function () {
      this.checkSlugIsUnique(this.form.lang, this.form.slug)
    },
  },
  created() {
    this.form.lang = 'fa'
    this.fetchServiceCategories(this.form.lang)
    this.fetchUsersByRole({
      role: 'editor',
    }).then(res => {
      this.editorOptions = res
    })
    this.fetchUsersByRole({
      role: 'writer',
    }).then(res => {
      this.writerOptions = res
    })
    this.fetchTags('fa')
  },
  methods: {
    optionalTagsHint() { return serviceOptionalTagsHint(this.$i18n.locale) },
    updateAltImage() {
      if (!this.form.serviceCoverImageAlt) {
        this.$set(this.form, 'serviceCoverImageAlt', this.form.title)
      }
      if (!this.form.serviceThumbnailAlt) {
        this.$set(this.form, 'serviceThumbnailAlt', this.form.title)
      }
    },
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    changeSelectedLanguage(selectedLanguage) {
      this.form.lang = selectedLanguage
      this.fetchServiceCategories(selectedLanguage)
      this.fetchTags(selectedLanguage)
    },
    searchCategories(search) {
      this.fetchPageCategories(this.form.lang, search)
    },
    submitForm() {
      this.$refs.sunEditor.sendValToForm()
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      const uploadList = ['serviceCoverImage', 'serviceThumbnail']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    handleShowOnHomeChange(value) {
      this.$set(this.form, 'showOnHome', value)
    },
    sendData(form) {
      const data = {
        title: form.title,
        slug: form.slug,
        showOnHome: !!form.showOnHome,
        watermark: this.form.watermark != null,
        sort: form.sort === '' || form.sort == null ? null : Number(form.sort),
        keywords: form.keywords,
        description: form.description,
        content: form.content,
        serviceCategoryId: this.form.category._id,
        editorId: this.form.editor ? this.form.editor._id : null,
        writerId: this.form.writer ? this.form.writer._id : null,
        tagIds: this.createTagIdArray(this.form.tags, this.newTagIds),
        serviceCoverImageFileId: form.serviceCoverImage ? form.serviceCoverImage._id : null,
        serviceThumbnailFileId: form.serviceThumbnail ? form.serviceThumbnail._id : null,
      }
      this.addItem(data, this.form.lang)
    },
  },
}
</script>
